export type SignatureImageType = 'image/png' | 'image/jpeg' | 'image/bmp' | 'image/webp';

export const ALLOWED_SIGNATURE_IMAGE_LABEL = 'PNG, JPEG, BMP, WEBP';

/** Detects the real image type from magic bytes; the extension and browser-reported MIME are not trusted. */
export const sniffImageType = async (file: Blob): Promise<SignatureImageType | null> =>
  sniffImageBytes(new Uint8Array(await file.slice(0, 12).arrayBuffer()));

export const sniffImageBytes = (bytes: Uint8Array): SignatureImageType | null => {
  if (bytes.length < 4) return null;
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'image/png';
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  if (bytes[0] === 0x42 && bytes[1] === 0x4d) return 'image/bmp';
  const ascii = (start: number, end: number) => String.fromCharCode(...bytes.slice(start, end));
  if (bytes.length >= 12 && ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return 'image/webp';
  return null;
};

export const isPdfBytes = (bytes: Uint8Array) =>
  bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;

export interface TrimmedSignature {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

/** Crops a drawing canvas to the bounding box of its painted pixels plus `padding`. Null when blank. */
export const trimCanvas = (source: HTMLCanvasElement, padding = 8): TrimmedSignature | null => {
  const ctx = source.getContext('2d', { willReadFrequently: true });
  const { width, height } = source;
  if (!ctx || !width || !height) return null;

  const { data } = ctx.getImageData(0, 0, width, height);
  let top = height;
  let left = width;
  let right = -1;
  let bottom = -1;

  for (let y = 0; y < height; y++) {
    const row = y * width * 4;
    for (let x = 0; x < width; x++) {
      if (data[row + x * 4 + 3] === 0) continue;
      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }
  if (right < 0) return null;

  const cropWidth = right - left + 1;
  const cropHeight = bottom - top + 1;
  const canvas = document.createElement('canvas');
  canvas.width = cropWidth + padding * 2;
  canvas.height = cropHeight + padding * 2;
  canvas.getContext('2d')?.drawImage(source, left, top, cropWidth, cropHeight, padding, padding, cropWidth, cropHeight);
  return { canvas, width: canvas.width, height: canvas.height };
};

export const canvasToPngFile = (canvas: HTMLCanvasElement, name: string) =>
  new Promise<File>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(new File([blob], name, { type: 'image/png' }));
      else reject(new Error('Failed to export signature image'));
    }, 'image/png');
  });

export const base64ToBytes = (base64: string) => {
  const clean = base64.replace(/^data:[^,]*,/, '').replace(/\s/g, '');
  const binary = atob(clean);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
};
